import { Link } from "react-router-dom";
import { Globe, ArrowRight } from "lucide-react";
import { pathFromKind, kindByKey } from "@/lib/ecosystem";
import { decodeHtmlEntities } from "@/lib/text";
import SmartImage from "@/components/SmartImage";

/**
 * Compact "company card" for the Ecosystem grid.
 *
 * Layout:
 *   ┌────────────────────────────────────┐
 *   │ [logo] Name                Active  │   ← header
 *   │        Category badge      badges  │
 *   │ ─────────────────────────────────  │
 *   │ description (line-clamp 3)         │
 *   │ #tag1 #tag2 #tag3 +N               │
 *   │ ─────────────────────────────────  │
 *   │ Website ↗            Details →     │   ← footer
 *   └────────────────────────────────────┘
 *
 * All blocks gracefully hide when fields are missing.
 */
const ActivityBadges = ({ activity, lang }) => {
  if (!activity || activity.total === 0) return null;
  // Each entry: { n, singular, plural } — `news` is its own plural in EN.
  const items = [];
  if (activity.news > 0) items.push({
    n: activity.news,
    singular: lang === "fr" ? "actu" : "news",
    plural: lang === "fr" ? "actus" : "news",
  });
  if (activity.jobs > 0) items.push({ n: activity.jobs, singular: "job", plural: "jobs" });
  if (activity.alerts > 0) items.push({
    n: activity.alerts,
    singular: lang === "fr" ? "alerte" : "alert",
    plural: lang === "fr" ? "alertes" : "alerts",
  });
  return (
    <div data-testid="ecosystem-activity" className="flex flex-col items-end gap-1 shrink-0">
      <span className="inline-flex items-center gap-1 bg-[#00A878] text-white text-[9px] font-mono-dl uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm">
        <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
        {lang === "fr" ? `Actif ${activity.since_days}j` : `Active ${activity.since_days}d`}
      </span>
      <div className="flex flex-wrap gap-1 justify-end">
        {items.map((it, i) => (
          <span key={i} className="bg-[#F7F7F2] border border-[#E5E7EB] text-[#111827] text-[10px] font-mono-dl tracking-wider px-1.5 py-0.5 rounded-sm">
            {it.n} {it.n > 1 ? it.plural : it.singular}
          </span>
        ))}
      </div>
    </div>
  );
};

const EcosystemCard = ({ entity, idx = 0, lang = "en" }) => {
  const kind = kindByKey(entity.kind);
  const to = `/ecosystem/${pathFromKind(entity.kind)}/${entity.slug}`;
  const overline = lang === "fr" ? (kind?.overlineFr || "") : (kind?.overlineEn || "");
  const tags = entity.tags || [];
  const visibleTags = tags.slice(0, 3);
  const overflow = Math.max(0, tags.length - visibleTags.length);
  const initial = (entity.name || "?").slice(0, 1).toUpperCase();

  return (
    <article
      data-testid={`ecosystem-card-${idx}`}
      className="bg-white border border-[#E5E7EB] hover:border-[#111827] rounded-md transition-colors flex flex-col h-full"
    >
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <Link
          to={to}
          className="eco-logo-frame w-12 h-12 shrink-0 border border-[#E5E7EB] rounded-md overflow-hidden bg-[#F7F7F2]"
          aria-label={entity.name}
        >
          {entity.logo_url ? (
            <SmartImage
              src={entity.logo_url}
              type="ecosystem"
              alt={entity.name || ""}
              fit="contain"
              className="max-w-[80%] max-h-[80%]"
            />
          ) : (
            <span className="font-display text-xl text-[#9ca3af]">{initial}</span>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-[17px] leading-tight text-[#111827] truncate">
            <Link to={to} className="hover:text-[#00A878] transition-colors">{entity.name}</Link>
          </h3>
          {overline && (
            <div className="dl-overline text-[#00A878] mt-0.5">{overline}</div>
          )}
        </div>
        <ActivityBadges activity={entity.activity} lang={lang} />
      </div>

      {/* Description (plain text preview — HTML stripped for cleanliness,
          entities decoded so "S&amp;OP" displays as "S&OP"). */}
      {entity.description && (
        <p className="px-4 text-[13px] leading-relaxed text-[#4B5563] line-clamp-3">
          {decodeHtmlEntities(String(entity.description).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim())}
        </p>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="px-4 mt-3 flex flex-wrap gap-1.5">
          {visibleTags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] font-mono-dl uppercase tracking-wider px-1.5 py-0.5 bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB] rounded-sm"
            >
              {tag}
            </span>
          ))}
          {overflow > 0 && (
            <span className="text-[10px] font-mono-dl tracking-wider px-1.5 py-0.5 bg-white text-[#9ca3af] border border-[#E5E7EB] rounded-sm">
              +{overflow}
            </span>
          )}
        </div>
      )}

      {/* Spacer + footer */}
      <div className="flex-1" />
      <div className="px-4 pt-3 mt-3 pb-3 border-t border-[#E5E7EB] flex items-center justify-between text-[12px]">
        {entity.website ? (
          <a
            href={entity.website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid={`ecosystem-website-${entity.slug}`}
            className="inline-flex items-center gap-1 text-[#4B5563] hover:text-[#00A878] transition-colors"
          >
            <Globe size={12} /> Website
          </a>
        ) : (
          <span />
        )}
        <Link
          to={to}
          data-testid={`ecosystem-details-${entity.slug}`}
          className="inline-flex items-center gap-1 text-[#111827] hover:text-[#00A878] font-mono-dl uppercase text-[11px] tracking-wider"
        >
          {lang === "fr" ? "Détails" : "Details"} <ArrowRight size={12} />
        </Link>
      </div>
    </article>
  );
};

export default EcosystemCard;
